// WebsiteContent — unified "Website Content" tab for Kaily to self-serve
// hero/portrait images across the public site. Each slot shares the same
// uploader pattern: preview → crop (react-easy-crop) → save. We call the
// `/api/site-meta` endpoints exclusively; no direct R2 or Mongo access from
// here.
//
// Why one component covering 3 slots instead of 3 pages: Kaily asked for a
// single "Website Content" entry in Studio. Keeping the slots as sibling
// cards on the same page means she scrolls once instead of hunting through
// nested menus.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { studioHeaders } from "@/lib/studioAuth";
import { Link } from "react-router-dom";
import { Image as ImageIcon, Trash2, Upload, Save, ArrowRight, X, Monitor, Smartphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Studio endpoints rely on the httpOnly cookie — same-origin, so axios sends
// it automatically. We still pass studioHeaders() for forward compatibility.
const SITE_META = `${API}/site-meta`;

const SLOTS = [
    {
        key: "meet_kaily_image",
        cropKey: "meet_kaily_crop",
        label: "Meet Kaily · portrait",
        helper: "Replaces the large K. tile on the Services page's Meet Kaily section.",
        aspect: 4 / 5,
    },
    {
        key: "about_image",
        cropKey: "about_crop",
        label: "About · portrait",
        helper: "Portrait shown at the top of the public /about page. Overrides Meet Kaily portrait when set. 4:5 ratio recommended.",
        aspect: 4 / 5,
    },
];

// Hero gets its own multi-variant card because Kaily asked for independent
// Desktop + Mobile crops: a wide landscape frame for desktop and a tall
// portrait crop for phones, each with their own file + their own framing.
// The aspect ratios below match EXACTLY what Landing.jsx renders on each
// breakpoint, so the Studio preview is a 1:1 reflection of the live page:
//   • Desktop (md+): aspect 16/8   ← Landing.jsx `md:aspect-[16/8]`
//   • Mobile  (<sm): aspect 16/18  ← Landing.jsx base `aspect-[16/18]`
const HERO_VARIANTS = [
    {
        id: "desktop",
        label: "Desktop",
        slotKey: "hero_image",
        cropKey: "hero_image_crop",
        aspect: 16 / 8,
        hint: "Shown on tablets and desktops (768px wide and up). Landscape frame.",
    },
    {
        id: "mobile",
        label: "Mobile",
        slotKey: "hero_image_mobile",
        cropKey: "hero_image_mobile_crop",
        aspect: 16 / 18,
        hint: "Shown on phones (under 768px). Taller crop to match the vertical phone layout.",
    },
];

// Services page hero — Desktop + Mobile BOTH render at 4:5 on the live
// page (small side tile on desktop, big portrait block on mobile). The
// Studio preview matches 4:5 for both so the crop box reflects reality.
const SERVICES_HERO_VARIANTS = [
    {
        id: "desktop",
        label: "Desktop",
        slotKey: "services_hero_image",
        cropKey: "services_hero_crop",
        aspect: 4 / 5,
        hint: "Shown to the right of the Services page headline on tablets and desktops.",
    },
    {
        id: "mobile",
        label: "Mobile",
        slotKey: "services_hero_image_mobile",
        cropKey: "services_hero_mobile_crop",
        aspect: 4 / 5,
        hint: "Shown between the headline and the sub-text on phones.",
    },
];


function CropModal({ open, imageSrc, aspect, initialCrop, title, onCancel, onConfirm }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
    // react-easy-crop uses pixel crop internally but we only persist the
    // framing centre (x/y as 0-100) + zoom so the public page can mirror it
    // via object-position without re-cropping the raw file. The original
    // upload is preserved untouched on R2 — no re-encoding, no colour
    // changes, no auto-generated background.
    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(initialCrop?.zoom || 1);
        }
    }, [open, initialCrop]);

    const centerFromPixelCrop = useCallback((px) => {
        // px: {x,y,width,height} in natural image pixels (whole image).
        // We derive the viewer centre as a percentage.
        const cx = px.x + px.width / 2;
        const cy = px.y + px.height / 2;
        // Normalise against the natural image size (approximate via media.naturalWidth/Height).
        const img = document.querySelector("img.rec-cropper-image");
        const nw = img?.naturalWidth || px.width;
        const nh = img?.naturalHeight || px.height;
        return {
            x: Math.round((cx / nw) * 1000) / 10,
            y: Math.round((cy / nh) * 1000) / 10,
        };
    }, []);

    const pixelCropRef = useRef(null);
    const onCropComplete = useCallback((_, pixelCrop) => {
        pixelCropRef.current = pixelCrop;
    }, []);

    if (!open) return null;
    return (
        <div
            data-testid="site-crop-modal"
            className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="bg-cafe-cream text-cafe-espresso max-w-3xl w-full rounded-xl overflow-hidden shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-cafe-espresso/10">
                    <div>
                        <p className="font-serif text-lg leading-tight">Adjust framing</p>
                        {title && (
                            <p className="text-[11px] uppercase tracking-[0.22em] text-cafe-olive mt-0.5" data-testid="crop-modal-context">
                                Editing: {title}
                            </p>
                        )}
                    </div>
                    <button onClick={onCancel} className="p-1 hover:bg-cafe-espresso/5 rounded"><X className="h-4 w-4" /></button>
                </div>
                <div className="relative bg-black" style={{ height: 440 }}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        showGrid={true}
                        restrictPosition={false}
                        classes={{ imageClassName: "rec-cropper-image" }}
                    />
                    {/* On-canvas hint for first-time Kaily — the crop viewport
                        can be dragged with mouse or touch to reframe. */}
                    <div className="pointer-events-none absolute left-3 bottom-3 text-[10px] uppercase tracking-[0.2em] text-cafe-cream/80 bg-black/50 px-2.5 py-1 rounded-full">
                        Drag to reposition · pinch / scroll to zoom
                    </div>
                </div>
                <div className="px-5 py-4 flex flex-col gap-3 border-t border-cafe-espresso/10">
                    <div className="flex items-center gap-3">
                        <span className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60 w-14">Zoom</span>
                        <input
                            type="range" min={1} max={4} step={0.05}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 accent-cafe-olive"
                            data-testid="crop-zoom"
                        />
                        <span className="text-xs text-cafe-espresso/60 w-10 text-right">{zoom.toFixed(2)}x</span>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" onClick={onCancel} data-testid="crop-cancel">Cancel</Button>
                        <Button
                            data-testid="crop-apply"
                            onClick={() => {
                                const px = pixelCropRef.current;
                                const centre = px ? centerFromPixelCrop(px) : { x: 50, y: 50 };
                                onConfirm({ ...centre, zoom: Math.round(zoom * 100) / 100 });
                            }}
                            className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                        >
                            Save framing
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function SlotCard({ slot, meta, onReload }) {
    const [uploading, setUploading] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [localPreview, setLocalPreview] = useState(null);
    const inputRef = useRef(null);
    const url = meta[slot.key];
    const crop = meta[slot.cropKey] || { x: 50, y: 50, zoom: 1 };

    async function doUpload(file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await axios.post(`${SITE_META}/upload/${slot.key}`, form, {
                headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            toast.success("Uploaded. Adjust framing or save as-is.");
            // Open crop modal with the newly uploaded URL as source.
            setLocalPreview(data.url);
            setCropOpen(true);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    async function saveCrop(box) {
        try {
            await axios.patch(SITE_META, { [slot.cropKey]: box }, { headers: studioHeaders(), withCredentials: true });
            toast.success("Framing saved");
            setCropOpen(false);
            setLocalPreview(null);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    }

    async function clearSlot() {
        if (!window.confirm("Remove this image? It will fall back to the plain placeholder on the public site.")) return;
        try {
            await axios.delete(`${SITE_META}/${slot.key}`, { headers: studioHeaders(), withCredentials: true });
            toast.success("Cleared");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Clear failed");
        }
    }

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid={`slot-card-${slot.key}`}>
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <p className="font-serif text-xl text-cafe-espresso">{slot.label}</p>
                    <p className="text-sm text-cafe-espresso/60 mt-1 max-w-md">{slot.helper}</p>
                </div>
                <ImageIcon className="h-5 w-5 text-cafe-olive flex-shrink-0" />
            </div>

            <div
                className="relative w-full bg-cafe-olive/10 rounded-lg overflow-hidden mb-4 border border-cafe-espresso/5"
                style={{ aspectRatio: slot.aspect }}
                data-testid={`slot-preview-${slot.key}`}
            >
                {url ? (
                    <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${crop.x}% ${crop.y}%` }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-cafe-olive/70">
                        <span className="text-xs uppercase tracking-[0.2em]">No image uploaded</span>
                    </div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => doUpload(e.target.files?.[0])}
                data-testid={`slot-file-${slot.key}`}
            />

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`slot-upload-${slot.key}`}
                    className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading…" : url ? "Replace image" : "Upload image"}
                </Button>
                {url && (
                    <>
                        <Button
                            variant="outline"
                            onClick={() => { setLocalPreview(url); setCropOpen(true); }}
                            data-testid={`slot-crop-${slot.key}`}
                            className="border-cafe-espresso/20"
                        >
                            Adjust framing
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={clearSlot}
                            data-testid={`slot-clear-${slot.key}`}
                            className="text-red-700 hover:text-red-900 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                        </Button>
                    </>
                )}
            </div>

            <CropModal
                open={cropOpen}
                imageSrc={localPreview}
                aspect={slot.aspect}
                initialCrop={crop}
                title={slot.label}
                onCancel={() => { setCropOpen(false); setLocalPreview(null); }}
                onConfirm={saveCrop}
            />
        </div>
    );
}


// Hero image gets a dedicated card with Desktop + Mobile tabs. Each tab is
// an independent upload + crop, saved to its own site_meta slot. The
// Studio preview uses the exact aspect the live page renders at (16:8 on
// desktop, 16:18 on mobile) so what Kaily sees here matches what visitors
// see on slowcafe.my.
//
// The component is parametric on `variants + title + helper + testIdPrefix`
// so the Services page hero can reuse the same UI (4:5 crop on both
// breakpoints) without a copy-paste.
function DualVariantImageCard({ variants, meta, onReload, title, helper, testIdPrefix }) {
    const [activeId, setActiveId] = useState(variants[0].id);
    const [cropOpen, setCropOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [localPreview, setLocalPreview] = useState(null);
    const inputRef = useRef(null);

    const active = variants.find((v) => v.id === activeId);
    const url = meta[active.slotKey];
    const crop = meta[active.cropKey] || { x: 50, y: 50, zoom: 1 };

    async function doUpload(file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await axios.post(`${SITE_META}/upload/${active.slotKey}`, form, {
                headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            toast.success(`${active.label} image uploaded. Reposition and save.`);
            setLocalPreview(data.url);
            setCropOpen(true);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    async function saveCrop(box) {
        try {
            // Only patch the active variant's crop field — this is critical:
            // adjusting Desktop must NOT overwrite Mobile and vice versa.
            await axios.patch(SITE_META, { [active.cropKey]: box }, { headers: studioHeaders(), withCredentials: true });
            toast.success(`${active.label} framing saved`);
            setCropOpen(false);
            setLocalPreview(null);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    }

    async function clearActive() {
        if (!window.confirm(`Remove the ${active.label} image? The ${active.label === "Desktop" ? "mobile" : "desktop"} variant is not affected.`)) return;
        try {
            await axios.delete(`${SITE_META}/${active.slotKey}`, { headers: studioHeaders(), withCredentials: true });
            toast.success(`${active.label} image cleared`);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Clear failed");
        }
    }

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid={`slot-card-${testIdPrefix}`}>
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <p className="font-serif text-xl text-cafe-espresso">{title}</p>
                    <p className="text-sm text-cafe-espresso/60 mt-1 max-w-md">{helper}</p>
                </div>
                <ImageIcon className="h-5 w-5 text-cafe-olive flex-shrink-0" />
            </div>

            {/* Device tabs — Desktop / Mobile. Selected tab controls which
                slot the upload / crop / preview below talks to. */}
            <div className="flex items-center gap-1 p-1 bg-cafe-cream rounded-full inline-flex mb-4" role="tablist">
                {variants.map((v) => (
                    <button
                        key={v.id}
                        role="tab"
                        aria-selected={activeId === v.id}
                        onClick={() => setActiveId(v.id)}
                        data-testid={`${testIdPrefix}-variant-${v.id}`}
                        className={[
                            "inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] uppercase tracking-[0.18em] transition-colors",
                            activeId === v.id
                                ? "bg-cafe-espresso text-cafe-cream"
                                : "text-cafe-espresso/60 hover:text-cafe-espresso",
                        ].join(" ")}
                    >
                        {v.id === "desktop" ? <Monitor className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Smartphone className="h-3.5 w-3.5" strokeWidth={1.75} />}
                        {v.label}
                    </button>
                ))}
            </div>

            {/* Context strip tells Kaily EXACTLY what she's editing right now */}
            <div className="text-[11px] uppercase tracking-[0.22em] text-cafe-olive mb-3" data-testid={`${testIdPrefix}-variant-context`}>
                Currently editing: {active.label} · {active.hint}
            </div>

            {/* Preview — aspect matches the live page for the active breakpoint */}
            <div
                className="relative w-full bg-cafe-olive/10 rounded-lg overflow-hidden mb-4 border border-cafe-espresso/5 mx-auto"
                style={{
                    aspectRatio: active.aspect,
                    // Mobile preview visually matches the actual phone frame
                    // width (narrower) so Kaily doesn't mistake a tall image
                    // for a wide one.
                    maxWidth: active.id === "mobile" ? 320 : undefined,
                }}
                data-testid={`${testIdPrefix}-preview-${active.id}`}
            >
                {url ? (
                    <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${crop.x}% ${crop.y}%` }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-cafe-olive/70">
                        <span className="text-xs uppercase tracking-[0.2em]">No {active.label.toLowerCase()} image uploaded</span>
                    </div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => doUpload(e.target.files?.[0])}
                data-testid={`${testIdPrefix}-file-${active.id}`}
            />

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testIdPrefix}-upload-${active.id}`}
                    className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading…" : url ? `Replace ${active.label.toLowerCase()} image` : `Upload ${active.label.toLowerCase()} image`}
                </Button>
                {url && (
                    <>
                        <Button
                            variant="outline"
                            onClick={() => { setLocalPreview(url); setCropOpen(true); }}
                            data-testid={`${testIdPrefix}-crop-${active.id}`}
                            className="border-cafe-espresso/20"
                        >
                            Adjust framing
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={clearActive}
                            data-testid={`${testIdPrefix}-clear-${active.id}`}
                            className="text-red-700 hover:text-red-900 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                        </Button>
                    </>
                )}
            </div>

            <CropModal
                open={cropOpen}
                imageSrc={localPreview}
                aspect={active.aspect}
                initialCrop={crop}
                title={`${title} — ${active.label}`}
                onCancel={() => { setCropOpen(false); setLocalPreview(null); }}
                onConfirm={saveCrop}
            />
        </div>
    );
}


// Backwards-compatible shortcut that keeps the previous testIdPrefix
// (`hero`) stable for existing tests + the long-standing data-testid
// surface area on the Home · Hero card.
function HeroDualVariantCard({ meta, onReload }) {
    return (
        <DualVariantImageCard
            variants={HERO_VARIANTS}
            meta={meta}
            onReload={onReload}
            title="Home · Hero image"
            helper="The large film-strip visual at the top of the homepage. Desktop and mobile use separate crops — set each independently so neither gets stretched."
            testIdPrefix="hero"
        />
    );
}

function ServicesHeroDualVariantCard({ meta, onReload }) {
    return (
        <DualVariantImageCard
            variants={SERVICES_HERO_VARIANTS}
            meta={meta}
            onReload={onReload}
            title="Services page · hero"
            helper="The portrait tile on the Photo/Video Services page. Desktop and mobile each get their own file + framing — pick a focal point (person, umbrella, dish) that lands in frame on each device."
            testIdPrefix="services-hero"
        />
    );
}


function HeroVideoCard({ meta, onReload }) {
    return (
        <VideoSlotCard
            meta={meta}
            onReload={onReload}
            slot="hero_video_url_mobile"
            cropKey="hero_video_mobile_crop"
            playbackSlot="hero_video_playback_mobile"
            title="Home · Hero reel (mobile only)"
            helper="Upload a portrait MP4 / MOV / WebM. Autoplays muted and looped on phones. Original aspect ratio is preserved — the ffmpeg web copy keeps the same framing. The mobile hero photo (above) is used as the loading poster and the autoplay fallback."
            aspectRatio={9 / 16}
            previewWidth={260}
            cropLabel="Mobile hero reel · 9:16"
            testIdPrefix="hero-video"
        />
    );
}

function HeroVideoDesktopCard({ meta, onReload }) {
    return (
        <VideoSlotCard
            meta={meta}
            onReload={onReload}
            slot="hero_video_url_desktop"
            cropKey="hero_video_desktop_crop"
            playbackSlot="hero_video_playback_desktop"
            title="Home · Hero reel (desktop)"
            helper="Optional landscape MP4 / MOV / WebM. Plays behind the confirmed desktop photo hero as a progressive enhancement — if the browser can't autoplay or no video is uploaded, the current desktop photo layout is shown unchanged."
            aspectRatio={16 / 9}
            previewWidth={420}
            cropLabel="Desktop hero reel · 16:9"
            testIdPrefix="hero-video-desktop"
        />
    );
}

// Shared video upload card — presigned R2 direct upload (up to 500 MB),
// XHR progress reporting, retry on failure, size + status readouts, and
// a separate "web playback copy" sub-slot (auto-filled by ffmpeg when
// available, or manually uploaded by Kaily when it isn't).
function VideoSlotCard({ meta, onReload, slot, cropKey, playbackSlot, title, helper, aspectRatio, previewWidth, cropLabel, testIdPrefix }) {
    const originalUrl = meta[slot] || "";
    const playbackUrl = meta[playbackSlot] || "";
    const playbackStatus = meta[`${playbackSlot}_status`] || "";
    const playbackSize = meta[`${playbackSlot}_size`] || 0;
    const crop = meta[cropKey] || { x: 50, y: 50, zoom: 1 };
    const displayUrl = playbackUrl || originalUrl; // prefer web copy

    const [cropOpen, setCropOpen] = useState(false);
    const [uploadState, setUploadState] = useState({ status: "idle", pct: 0, size: 0, error: null, file: null });
    const originalInputRef = useRef(null);
    const playbackInputRef = useRef(null);

    // Smart upload path:
    //   1. ≤ 100 MB → server proxy (POST /upload-video/{slot}) — no CORS needed.
    //   2. > 100 MB → try presigned direct-to-R2 (needs R2 bucket CORS).
    //      If the direct PUT hits a CORS/network error AND the file is
    //      actually still ≤ 100 MB fallback to the server proxy automatically.
    // Keeps the previous saved video intact on failure.
    async function directUpload(file, targetSlot) {
        if (!file) return;
        if (!/^video\//i.test(file.type || "")) {
            toast.error("Please choose a video file (MP4 / MOV / WebM)");
            return;
        }
        if (file.size > 500 * 1024 * 1024) {
            toast.error(`File is ${(file.size / (1024 * 1024)).toFixed(1)} MB — max 500 MB per upload`);
            return;
        }

        // ---- small-file server proxy (no CORS requirement) ----
        async function serverProxyUpload() {
            const form = new FormData();
            form.append("file", file);
            const xhr = new XMLHttpRequest();
            await new Promise((resolve, reject) => {
                xhr.open("POST", `${SITE_META}/upload-video/${targetSlot}`);
                // Studio password header + cookie fallback.
                const pwd = (typeof studioHeaders === "function" ? studioHeaders() : {}) || {};
                Object.entries(pwd).forEach(([k, v]) => xhr.setRequestHeader(k, v));
                xhr.withCredentials = true;
                xhr.upload.onprogress = (ev) => ev.lengthComputable && setUploadState((s) => ({ ...s, pct: Math.round((ev.loaded / ev.total) * 100) }));
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else {
                        let msg = `Server responded ${xhr.status}`;
                        try { msg = JSON.parse(xhr.responseText || "{}").detail || msg; } catch {}
                        reject(new Error(msg));
                    }
                };
                xhr.onerror = () => reject(new Error("Network error during server upload"));
                xhr.send(form);
            });
            setUploadState({ status: "done", pct: 100, size: file.size, error: null, file });
            toast.success("Reel uploaded");
            await onReload();
        }

        setUploadState({ status: "signing", pct: 0, size: file.size, error: null, file });

        // Small file → go straight through the proxy. No presign, no CORS risk.
        if (file.size <= 100 * 1024 * 1024) {
            try {
                setUploadState((s) => ({ ...s, status: "uploading", pct: 0 }));
                await serverProxyUpload();
                return;
            } catch (err) {
                const msg = err?.response?.data?.detail || err?.message || "Upload failed";
                setUploadState((s) => ({ ...s, status: "error", error: msg }));
                toast.error("Upload failed", { description: msg });
                return;
            }
        }

        // Large file → presigned direct-to-R2. On CORS/network error fall
        // back to the proxy (if still within its 100 MB cap), otherwise show
        // the CORS instruction.
        const presignEndpoint = `${SITE_META}/presign-upload/${targetSlot}`;
        const confirmEndpoint = `${SITE_META}/confirm-upload/${targetSlot}`;
        try {
            const { data: presign } = await axios.post(
                presignEndpoint,
                { content_type: file.type || "video/mp4", size: file.size, filename: file.name },
                { headers: studioHeaders(), withCredentials: true },
            );
            setUploadState((s) => ({ ...s, status: "uploading", pct: 0 }));
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", presign.upload_url);
                xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
                xhr.upload.onprogress = (ev) => ev.lengthComputable && setUploadState((s) => ({ ...s, pct: Math.round((ev.loaded / ev.total) * 100) }));
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 responded ${xhr.status}: ${xhr.responseText?.slice(0, 200)}`)));
                xhr.onerror = () => reject(new Error("CORS_OR_NETWORK"));
                xhr.ontimeout = () => reject(new Error("Upload timed out"));
                xhr.send(file);
            });
            setUploadState((s) => ({ ...s, status: "confirming", pct: 100 }));
            const { data: confirm } = await axios.post(
                confirmEndpoint,
                { key: presign.key, size: file.size },
                { headers: studioHeaders(), withCredentials: true },
            );
            setUploadState({ status: "done", pct: 100, size: confirm.size || file.size, error: null, file });
            toast.success(targetSlot === playbackSlot ? "Playback copy uploaded" : "Reel uploaded");
            await onReload();
        } catch (err) {
            const raw = err?.response?.data?.detail || err?.message || "Upload failed";
            const corsHint = "R2 bucket CORS policy is not set for this origin. Apply the CORS JSON in Cloudflare → R2 → slowcafe-kit → Settings → CORS Policy. Allowed origins must include this site. For files ≤ 100 MB the server proxy is available as a fallback.";
            const msg = raw === "CORS_OR_NETWORK" ? corsHint : raw;
            setUploadState((s) => ({ ...s, status: "error", error: msg }));
            toast.error("Upload failed", { description: msg.slice(0, 160) });
        }
    }

    function retry() {
        if (uploadState.file) {
            // Infer target slot from the error metadata — defaults to original.
            directUpload(uploadState.file, slot);
        } else {
            originalInputRef.current?.click();
        }
    }

    async function saveCrop(box) {
        try {
            await axios.patch(SITE_META, { [cropKey]: box }, { headers: studioHeaders(), withCredentials: true });
            toast.success("Framing saved");
            setCropOpen(false);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    }

    async function clearOriginal() {
        if (!window.confirm("Remove the uploaded reel? The paired photo (if any) will remain as the hero.")) return;
        try {
            await axios.delete(`${SITE_META}/${slot}`, { headers: studioHeaders(), withCredentials: true });
            // Also wipe playback copy so Studio reflects reality.
            await axios.delete(`${SITE_META}/${slot}/playback`, { headers: studioHeaders(), withCredentials: true });
            toast.success("Reel cleared");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Clear failed");
        }
    }

    async function clearPlayback() {
        if (!window.confirm("Clear the web playback copy? The frontend will fall back to the original file until ffmpeg produces a new copy.")) return;
        try {
            await axios.delete(`${SITE_META}/${slot}/playback`, { headers: studioHeaders(), withCredentials: true });
            toast.success("Playback copy cleared");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Clear failed");
        }
    }

    async function retryTranscode() {
        try {
            await axios.post(`${SITE_META}/${slot}/retry-transcode`, {}, { headers: studioHeaders(), withCredentials: true });
            toast.info("Re-queued transcode — refresh in a moment");
            setTimeout(onReload, 2000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Retry failed");
        }
    }

    const uploading = ["signing", "uploading", "confirming"].includes(uploadState.status);
    const prettyBytes = (b) => {
        if (!b) return "";
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
        return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid={`slot-card-${testIdPrefix}`}>
            <p className="font-serif text-xl text-cafe-espresso">{title}</p>
            <p className="text-sm text-cafe-espresso/60 mt-1 max-w-xl">{helper}</p>

            {/* Preview — mirrors the live hero. */}
            <div
                className="relative w-full bg-black overflow-hidden rounded-lg mx-auto mt-5 mb-4"
                style={{ aspectRatio, maxWidth: previewWidth }}
                data-testid={`${testIdPrefix}-preview`}
            >
                {displayUrl ? (
                    <video
                        src={displayUrl}
                        muted autoPlay loop playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: `${crop.x}% ${crop.y}%` }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-cafe-cream/60">
                        <span className="text-[11px] uppercase tracking-[0.22em]">No reel uploaded</span>
                    </div>
                )}
            </div>

            {/* File size + status readouts */}
            {originalUrl && (
                <div className="mb-4 text-[12px] text-cafe-espresso/70 space-y-1">
                    <div data-testid={`${testIdPrefix}-info`}>
                        <span className="uppercase tracking-[0.18em] text-cafe-olive">Original</span> · stored on R2.
                        {playbackStatus === "ready" && playbackSize > 0 && (
                            <> <span className="uppercase tracking-[0.18em] text-cafe-olive ml-2">Web copy</span> · {prettyBytes(playbackSize)}.</>
                        )}
                        {playbackStatus === "processing" && <span className="ml-2 text-amber-700">Web copy transcoding…</span>}
                        {playbackStatus === "pending" && <span className="ml-2 text-amber-700">Web copy queued…</span>}
                        {playbackStatus === "unsupported_no_ffmpeg" && <span className="ml-2 text-amber-700">ffmpeg unavailable on this environment — upload a web-friendly MP4 manually below.</span>}
                        {playbackStatus.startsWith("failed:") && <span className="ml-2 text-red-700">Web copy failed · {playbackStatus.replace("failed:", "")}</span>}
                    </div>
                </div>
            )}

            {/* Live upload progress / error */}
            {uploadState.status !== "idle" && uploadState.status !== "done" && (
                <div className="mb-4 rounded-lg bg-cafe-latte/60 p-3" data-testid={`${testIdPrefix}-progress`}>
                    {uploadState.status === "signing" && <p className="text-sm text-cafe-espresso">Requesting upload URL…</p>}
                    {uploadState.status === "uploading" && (
                        <>
                            <p className="text-sm text-cafe-espresso">Uploading · {uploadState.pct}% of {prettyBytes(uploadState.size)}</p>
                            <div className="mt-2 h-2 bg-cafe-espresso/10 rounded overflow-hidden">
                                <div className="h-full bg-cafe-olive transition-all" style={{ width: `${uploadState.pct}%` }} />
                            </div>
                        </>
                    )}
                    {uploadState.status === "confirming" && <p className="text-sm text-cafe-espresso">Finalising on backend…</p>}
                    {uploadState.status === "error" && (
                        <div className="flex items-start gap-3">
                            <div className="flex-1">
                                <p className="text-sm text-red-700 font-semibold">Upload failed</p>
                                <p className="text-xs text-red-700/80 mt-1 break-words">{uploadState.error}</p>
                                <p className="text-[11px] text-cafe-espresso/60 mt-1">Previous saved reel (if any) is preserved — nothing was overwritten.</p>
                            </div>
                            <Button size="sm" onClick={retry} className="bg-cafe-espresso text-cafe-cream" data-testid={`${testIdPrefix}-retry`}>Retry</Button>
                        </div>
                    )}
                </div>
            )}

            <input
                ref={originalInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/*"
                className="hidden"
                onChange={(e) => directUpload(e.target.files?.[0], slot)}
                data-testid={`${testIdPrefix}-file`}
            />
            <input
                ref={playbackInputRef}
                type="file"
                accept="video/mp4"
                className="hidden"
                onChange={(e) => directUpload(e.target.files?.[0], playbackSlot)}
                data-testid={`${testIdPrefix}-playback-file`}
            />
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    onClick={() => originalInputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testIdPrefix}-upload`}
                    className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading…" : originalUrl ? "Replace reel" : "Upload reel"}
                </Button>
                {originalUrl && (
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setCropOpen(true)}
                            data-testid={`${testIdPrefix}-crop`}
                            className="border-cafe-espresso/20"
                        >
                            Adjust framing
                        </Button>
                        {playbackStatus === "unsupported_no_ffmpeg" || playbackStatus.startsWith("failed:") || playbackStatus === "" ? (
                            <Button
                                variant="outline"
                                onClick={() => playbackInputRef.current?.click()}
                                disabled={uploading}
                                data-testid={`${testIdPrefix}-playback-upload`}
                                className="border-cafe-espresso/20"
                            >
                                <Upload className="h-4 w-4 mr-2" /> Upload web copy manually
                            </Button>
                        ) : null}
                        {playbackStatus.startsWith("failed:") && (
                            <Button
                                variant="outline"
                                onClick={retryTranscode}
                                data-testid={`${testIdPrefix}-retry-transcode`}
                                className="border-cafe-espresso/20"
                            >
                                Retry transcode
                            </Button>
                        )}
                        {playbackUrl && (
                            <Button
                                variant="ghost"
                                onClick={clearPlayback}
                                data-testid={`${testIdPrefix}-playback-clear`}
                                className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                            >
                                Clear web copy
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            onClick={clearOriginal}
                            data-testid={`${testIdPrefix}-clear`}
                            className="text-red-700 hover:text-red-900 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Remove reel
                        </Button>
                    </>
                )}
            </div>

            <VideoCropModal
                open={cropOpen}
                videoUrl={displayUrl}
                initialCrop={crop}
                label={cropLabel}
                aspectRatio={aspectRatio}
                onCancel={() => setCropOpen(false)}
                onConfirm={saveCrop}
            />
        </div>
    );
}


// Lightweight crop editor for the mobile hero video. We don't use
// react-easy-crop here because it only accepts <img>; instead we show the
// actual <video> at 9:16 with a draggable focal-point marker + zoom slider.
// The user's drag position maps to object-position percentage so the live
// mobile hero mirrors it exactly.
function VideoCropModal({ open, videoUrl, initialCrop, onCancel, onConfirm, label = "Mobile hero reel · 9:16", aspectRatio = 9 / 16 }) {
    const [pos, setPos] = useState({ x: 50, y: 50 });
    const [zoom, setZoom] = useState(1);
    const videoRef = useRef(null);
    const dragging = useRef(false);

    useEffect(() => {
        if (open) {
            setPos({ x: initialCrop?.x ?? 50, y: initialCrop?.y ?? 50 });
            setZoom(initialCrop?.zoom ?? 1);
        }
    }, [open, initialCrop]);

    if (!open) return null;

    const onPointerDown = (e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); };
    const onPointerUp = (e) => { dragging.current = false; e.currentTarget.releasePointerCapture?.(e.pointerId); };
    const onPointerMove = (e) => {
        if (!dragging.current) return;
        const r = e.currentTarget.getBoundingClientRect();
        const nx = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
        const ny = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
        setPos({ x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 });
    };

    return (
        <div
            data-testid="video-crop-modal"
            className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="bg-cafe-cream text-cafe-espresso max-w-md w-full rounded-xl overflow-hidden shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-cafe-espresso/10">
                    <div>
                        <p className="font-serif text-lg leading-tight">Adjust framing</p>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-cafe-olive mt-0.5">
                            {label}
                        </p>
                    </div>
                    <button onClick={onCancel} className="p-1 hover:bg-cafe-espresso/5 rounded"><X className="h-4 w-4" /></button>
                </div>
                <div
                    className="relative bg-black overflow-hidden mx-auto my-5 touch-none cursor-grab active:cursor-grabbing"
                    style={{ aspectRatio, width: 240 }}
                    onPointerDown={onPointerDown}
                    onPointerUp={onPointerUp}
                    onPointerMove={onPointerMove}
                    onPointerCancel={onPointerUp}
                >
                    {videoUrl && (
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            muted autoPlay loop playsInline
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ objectPosition: `${pos.x}% ${pos.y}%`, transform: `scale(${zoom})`, transformOrigin: `${pos.x}% ${pos.y}%` }}
                        />
                    )}
                    {/* Crosshair showing current focal point */}
                    <div
                        className="pointer-events-none absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-cafe-cream shadow-[0_0_0_2px_rgba(0,0,0,0.4)]"
                        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    />
                    <div className="pointer-events-none absolute left-2 bottom-2 text-[10px] uppercase tracking-[0.2em] text-cafe-cream/80 bg-black/50 px-2 py-0.5 rounded-full">
                        Drag to reposition
                    </div>
                </div>
                <div className="px-5 pb-4 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <span className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60 w-14">Zoom</span>
                        <input
                            type="range" min={1} max={3} step={0.05}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 accent-cafe-olive"
                            data-testid="video-crop-zoom"
                        />
                        <span className="text-xs text-cafe-espresso/60 w-10 text-right">{zoom.toFixed(2)}x</span>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" onClick={onCancel} data-testid="video-crop-cancel">Cancel</Button>
                        <Button
                            data-testid="video-crop-apply"
                            onClick={() => onConfirm({ x: pos.x, y: pos.y, zoom: Math.round(zoom * 100) / 100 })}
                            className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                        >
                            Save framing
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}


// ---------- International Showcase Reel + Poster + Caption ----------
// Separate card because /international shows a single showcase block below
// the olive hero. Section auto-hides on the public page when both the reel
// and the still are empty (Phase 6 "no empty placeholders" rule). No crop
// here — Kaily positions the clip via source cut, not viewer object-position.
function InternationalShowcaseCard({ meta, onReload }) {
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [caption, setCaption] = useState(meta.international_showcase_caption || "");
    const [savingCaption, setSavingCaption] = useState(false);
    const videoRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => { setCaption(meta.international_showcase_caption || ""); }, [meta.international_showcase_caption]);

    async function uploadVideo(file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setUploadingVideo(true);
        try {
            await axios.post(`${SITE_META}/upload-video/international_showcase_reel`, form, {
                headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" },
                withCredentials: true,
                timeout: 180000,
            });
            toast.success("Showcase reel uploaded");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingVideo(false);
            if (videoRef.current) videoRef.current.value = "";
        }
    }

    async function uploadImage(file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setUploadingImage(true);
        try {
            await axios.post(`${SITE_META}/upload/international_showcase_image`, form, {
                headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" },
                withCredentials: true,
                timeout: 60000,
            });
            toast.success("Poster image uploaded");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingImage(false);
            if (imageRef.current) imageRef.current.value = "";
        }
    }

    async function clearSlot(slot, label) {
        if (!window.confirm(`Remove the ${label}?`)) return;
        try {
            await axios.delete(`${SITE_META}/${slot}`, { headers: studioHeaders(), withCredentials: true });
            toast.success(`${label} cleared`);
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Clear failed");
        }
    }

    async function saveCaption() {
        setSavingCaption(true);
        try {
            await axios.patch(SITE_META, { international_showcase_caption: caption.trim() }, { headers: studioHeaders(), withCredentials: true });
            toast.success("Caption saved");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingCaption(false);
        }
    }

    const reelUrl = meta.international_showcase_reel || "";
    const posterUrl = meta.international_showcase_image || "";

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid="slot-card-international-showcase">
            <p className="font-serif text-xl text-cafe-espresso">International · Showcase reel</p>
            <p className="text-sm text-cafe-espresso/60 mt-1 max-w-xl">
                Appears on <code className="text-cafe-olive">/international</code> below the olive hero. Upload a reel (MP4 / MOV / WebM, max 60 MB) and an optional still (poster + fallback). Section is hidden on the public page until at least one asset is uploaded.
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Video */}
                <div className="rounded-lg border border-cafe-espresso/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-cafe-olive font-semibold mb-3">Reel (video)</p>
                    <div className="relative w-full bg-black overflow-hidden rounded-md mb-3" style={{ aspectRatio: 16 / 9 }}>
                        {reelUrl ? (
                            <video
                                src={reelUrl}
                                muted autoPlay loop playsInline
                                poster={posterUrl || undefined}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-cafe-cream/60">
                                <span className="text-[11px] uppercase tracking-[0.22em]">No reel uploaded</span>
                            </div>
                        )}
                    </div>
                    <input
                        ref={videoRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/*"
                        className="hidden"
                        onChange={(e) => uploadVideo(e.target.files?.[0])}
                        data-testid="intl-showcase-video-file"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            onClick={() => videoRef.current?.click()}
                            disabled={uploadingVideo}
                            data-testid="intl-showcase-video-upload"
                            className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {uploadingVideo ? "Uploading…" : reelUrl ? "Replace reel" : "Upload reel"}
                        </Button>
                        {reelUrl && (
                            <Button
                                variant="ghost"
                                onClick={() => clearSlot("international_showcase_reel", "showcase reel")}
                                data-testid="intl-showcase-video-clear"
                                className="text-red-700 hover:text-red-900 hover:bg-red-50"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                            </Button>
                        )}
                    </div>
                </div>

                {/* Poster / fallback image */}
                <div className="rounded-lg border border-cafe-espresso/10 p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-cafe-olive font-semibold mb-3">Poster image (optional)</p>
                    <div className="relative w-full bg-cafe-olive/15 overflow-hidden rounded-md mb-3" style={{ aspectRatio: 16 / 9 }}>
                        {posterUrl ? (
                            <img src={posterUrl} alt="Showcase poster" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-cafe-espresso/40">
                                <span className="text-[11px] uppercase tracking-[0.22em]">No still uploaded</span>
                            </div>
                        )}
                    </div>
                    <input
                        ref={imageRef}
                        type="file"
                        accept="image/*,image/heic,image/heif"
                        className="hidden"
                        onChange={(e) => uploadImage(e.target.files?.[0])}
                        data-testid="intl-showcase-image-file"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            onClick={() => imageRef.current?.click()}
                            disabled={uploadingImage}
                            data-testid="intl-showcase-image-upload"
                            className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {uploadingImage ? "Uploading…" : posterUrl ? "Replace still" : "Upload still"}
                        </Button>
                        {posterUrl && (
                            <Button
                                variant="ghost"
                                onClick={() => clearSlot("international_showcase_image", "poster still")}
                                data-testid="intl-showcase-image-clear"
                                className="text-red-700 hover:text-red-900 hover:bg-red-50"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Caption */}
            <div className="mt-5">
                <Label className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60">Short caption (optional, max 240 chars)</Label>
                <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="e.g. The Italian Evening · Restaurant commercial film"
                    maxLength={240}
                    data-testid="intl-showcase-caption"
                    className="mt-2"
                />
                <Button
                    onClick={saveCaption}
                    disabled={savingCaption}
                    data-testid="intl-showcase-caption-save"
                    className="mt-3 bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive"
                >
                    <Save className="h-4 w-4 mr-2" />
                    {savingCaption ? "Saving…" : "Save caption"}
                </Button>
            </div>
        </div>
    );
}




function MeetKailyTextCard({ meta, onReload }) {
    const [role, setRole] = useState(meta.meet_kaily_role || "");
    const [bio, setBio] = useState(meta.meet_kaily_bio || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setRole(meta.meet_kaily_role || "");
        setBio(meta.meet_kaily_bio || "");
    }, [meta.meet_kaily_role, meta.meet_kaily_bio]);
    async function save() {
        setSaving(true);
        try {
            await axios.patch(SITE_META, {
                meet_kaily_role: role.trim(),
                meet_kaily_bio: bio.trim(),
            }, { headers: studioHeaders(), withCredentials: true });
            toast.success("Meet Kaily copy saved");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    }
    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid="slot-card-meet-kaily-text">
            <p className="font-serif text-xl text-cafe-espresso">Meet Kaily · copy</p>
            <p className="text-sm text-cafe-espresso/60 mt-1 max-w-md">Role tagline + bio shown next to your portrait on the Services page.</p>
            <div className="mt-4 space-y-3">
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60">Role tagline</Label>
                    <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="KAILY GAN · FILMMAKER" data-testid="meet-kaily-role" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60">Bio</Label>
                    <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} data-testid="meet-kaily-bio" />
                </div>
                <Button onClick={save} disabled={saving} data-testid="meet-kaily-save" className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive">
                    <Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save copy"}
                </Button>
            </div>
        </div>
    );
}


function AboutContentCard({ meta, onReload }) {
    const [state, setState] = useState({
        about_role_en: meta.about_role_en || "",
        about_role_cn: meta.about_role_cn || "",
        about_bio_en: meta.about_bio_en || "",
        about_bio_cn: meta.about_bio_cn || "",
        about_approach_en: meta.about_approach_en || "",
        about_approach_cn: meta.about_approach_cn || "",
        about_process_en: meta.about_process_en || "",
        about_process_cn: meta.about_process_cn || "",
    });
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setState({
            about_role_en: meta.about_role_en || "",
            about_role_cn: meta.about_role_cn || "",
            about_bio_en: meta.about_bio_en || "",
            about_bio_cn: meta.about_bio_cn || "",
            about_approach_en: meta.about_approach_en || "",
            about_approach_cn: meta.about_approach_cn || "",
            about_process_en: meta.about_process_en || "",
            about_process_cn: meta.about_process_cn || "",
        });
    }, [meta.about_role_en, meta.about_role_cn, meta.about_bio_en, meta.about_bio_cn, meta.about_approach_en, meta.about_approach_cn, meta.about_process_en, meta.about_process_cn]);

    const set = (k) => (e) => setState((p) => ({ ...p, [k]: e.target.value }));

    async function save() {
        setSaving(true);
        try {
            const payload = {};
            Object.keys(state).forEach((k) => { payload[k] = (state[k] || "").trim(); });
            await axios.patch(SITE_META, payload, { headers: studioHeaders(), withCredentials: true });
            toast.success("About page copy saved");
            await onReload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    }

    const Section = ({ label, enKey, cnKey, rows = 3, placeholder = "" }) => (
        <div className="border-t border-cafe-espresso/10 pt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-cafe-espresso/60 mb-3">{label}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <Label className="text-[11px] text-cafe-espresso/60">English</Label>
                    {rows === 1
                        ? <Input value={state[enKey]} onChange={set(enKey)} placeholder={placeholder} data-testid={`about-${enKey}`} />
                        : <Textarea value={state[enKey]} onChange={set(enKey)} rows={rows} placeholder={placeholder} data-testid={`about-${enKey}`} />
                    }
                </div>
                <div>
                    <Label className="text-[11px] text-cafe-espresso/60">中文</Label>
                    {rows === 1
                        ? <Input value={state[cnKey]} onChange={set(cnKey)} placeholder={placeholder} data-testid={`about-${cnKey}`} />
                        : <Textarea value={state[cnKey]} onChange={set(cnKey)} rows={rows} placeholder={placeholder} data-testid={`about-${cnKey}`} />
                    }
                </div>
            </div>
        </div>
    );

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid="slot-card-about-text">
            <p className="font-serif text-xl text-cafe-espresso">About page · bilingual copy</p>
            <p className="text-sm text-cafe-espresso/60 mt-1 max-w-xl">Edit the public /about and /about/cn pages. Keep each pair in sync — the public page falls back to the English version when the Chinese is blank.</p>
            <div className="mt-4 space-y-5">
                <Section label="Role tagline" enKey="about_role_en" cnKey="about_role_cn" rows={1} placeholder="Independent F&B, hospitality & brand filmmaker" />
                <Section label="Short bio (hero)" enKey="about_bio_en" cnKey="about_bio_cn" rows={4} />
                <Section label="My role on your project" enKey="about_approach_en" cnKey="about_approach_cn" rows={4} />
                <Section label="How we work together" enKey="about_process_en" cnKey="about_process_cn" rows={5} />
                <Button onClick={save} disabled={saving} data-testid="about-text-save" className="bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive">
                    <Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save About copy"}
                </Button>
            </div>
        </div>
    );
}


function HeroFilmUploadDiagnosticsCard() {
    const [report, setReport] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    const [testResult, setTestResult] = React.useState(null);
    const [testing, setTesting] = React.useState(false);

    const load = async () => {
        setLoading(true); setError(null);
        try {
            const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/r2-cors-check`, { headers: studioHeaders(), withCredentials: true });
            setReport(r.data);
        } catch (err) {
            setError(err?.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => { load(); }, []);

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(report.recommended_cors_json, null, 2));
            setCopied(true); setTimeout(() => setCopied(false), 2000);
        } catch (_) {}
    };

    /* Live CORS test — tries a PUT to each bucket from the browser.
     *
     * This is the ONLY way to prove the bucket CORS actually lets THIS
     * origin PUT. The server-side `/r2-cors-check` is blocked by R2's
     * token-scope (AccessDenied on get_bucket_cors) so it can only tell
     * us the recommended JSON — not whether it has been applied. The
     * browser PUT tells us the truth.
     *
     * Flow per bucket:
     *  1. POST /r2-test-upload → backend returns two presigned PUT URLs
     *     + keys inside a `_cors-check/` prefix (never shown on the site).
     *  2. XHR PUT a tiny text blob to each URL. Classify failures:
     *       status === 0  → CORS blocked (preflight failed)
     *       status >= 400 → R2 rejected (signature, perms, etc.)
     *  3. POST /r2-test-verify so backend can prove the object actually
     *     landed, read its size, and delete it. If the browser said "OK"
     *     but the server can't find the file, something is very wrong.
     */
    const runTest = async () => {
        setTesting(true); setTestResult(null);
        const bust = (resolve, kind, extra = {}) => resolve({ ok: false, stage: kind, ...extra });
        try {
            const init = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/r2-test-upload`,
                {}, { headers: studioHeaders(), withCredentials: true });
            const body = init.data.expected_body;
            const puts = ["public", "drafts"].map((which) => new Promise((resolve) => {
                const slot = init.data[which];
                if (!slot?.upload_url) {
                    return bust(resolve, "presign", { error: "No upload URL returned for " + which });
                }
                const started = performance.now();
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", slot.upload_url);
                xhr.setRequestHeader("Content-Type", "text/plain");
                xhr.onload = () => {
                    const ms = Math.round(performance.now() - started);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        return resolve({ ok: true, stage: "put", httpStatus: xhr.status, ms });
                    }
                    resolve({
                        ok: false, stage: "put", httpStatus: xhr.status,
                        error: `R2 rejected the PUT (${xhr.status}): ${(xhr.responseText || "").slice(0, 180)}`,
                        ms,
                    });
                };
                xhr.onerror = () => {
                    resolve({
                        ok: false, stage: "put", httpStatus: xhr.status,
                        error: xhr.status === 0
                            ? "Browser blocked the PUT before it reached R2 — CORS not configured for this origin"
                            : "Network error during PUT",
                    });
                };
                xhr.send(new Blob([body], { type: "text/plain" }));
            }));
            const [publicPut, draftsPut] = await Promise.all(puts);
            // Server-side verify. Even if the PUT reported 2xx, this is the
            // ground truth: did the file actually appear on R2?
            let verify = {};
            try {
                const v = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/r2-test-verify`,
                    { public_key: init.data.public?.key, drafts_key: init.data.drafts?.key },
                    { headers: studioHeaders(), withCredentials: true });
                verify = v.data;
            } catch (err) {
                verify = { error: err?.response?.data?.detail || err.message };
            }
            setTestResult({
                public: { bucket: init.data.public?.bucket, put: publicPut, verify: verify.public },
                drafts: { bucket: init.data.drafts?.bucket, put: draftsPut, verify: verify.drafts },
            });
        } catch (err) {
            setTestResult({ fatal: err?.response?.data?.detail || err.message });
        } finally {
            setTesting(false);
        }
    };

    const bucketTestRow = (label, slot) => {
        if (!slot) return null;
        const put = slot.put || {};
        const vfy = slot.verify || {};
        const good = put.ok && vfy.uploaded;
        return (
            <div className={`rounded-lg border p-3 text-[12.5px] space-y-1 ${good ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="font-medium text-cafe-espresso">{label}</div>
                        <div className="font-mono text-[11px] text-cafe-espresso/60">{slot.bucket}</div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${good ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
                        {good ? "✓ PASS" : "✗ FAIL"}
                    </span>
                </div>
                <div className="pt-1 text-cafe-espresso/80">
                    <div>Browser PUT: {put.ok ? `✓ ${put.httpStatus} (${put.ms || "?"} ms)` : `✗ ${put.error || "unknown"}`}</div>
                    <div>Server verify: {vfy.uploaded ? `✓ size ${vfy.size ?? "?"} bytes, deleted ${vfy.deleted ? "✓" : "✗"}` : `✗ ${vfy.error || "not found"}`}</div>
                </div>
            </div>
        );
    };

    const bucketRow = (label, b) => (
        <div className="rounded-lg border border-cafe-espresso/15 bg-cafe-cream/40 p-3 text-sm">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-medium text-cafe-espresso">{label}</div>
                    <div className="font-mono text-[11px] text-cafe-espresso/60">{b.name || "(not configured)"}</div>
                </div>
                {b.configured ? (
                    <span className="inline-flex items-center text-[10.5px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">CORS 已配置</span>
                ) : (
                    <span className="inline-flex items-center text-[10.5px] font-medium bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">无法从服务器读取</span>
                )}
            </div>
            {b.error && <div className="mt-2 text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">{b.error}</div>}
            {b.covered && (
                <ul className="mt-2 space-y-1 text-[12px]">
                    {Object.entries(b.covered).map(([origin, ok]) => (
                        <li key={origin} className="flex items-center justify-between">
                            <span className="font-mono text-[11px] text-cafe-espresso/80">{origin}</span>
                            <span className={ok ? "text-emerald-700" : "text-red-600 font-medium"}>{ok ? "✓ 允许" : "✗ 未允许"}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid="hero-film-diagnostics">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="font-serif text-xl text-cafe-espresso">Hero Film 上传诊断 · R2 CORS</p>
                    <p className="text-sm text-cafe-espresso/60 mt-1 max-w-2xl">Case Study Hero Film 上传失败几乎总是 R2 bucket 的 CORS 未允许当前域名。这里显示生产环境的 R2 配置状态和需要在 Cloudflare 配置的 CORS JSON。</p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? "读取中…" : "刷新"}</Button>
            </div>

            {error && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{String(error)}</div>}
            {report && (
                <div className="mt-4 space-y-3">
                    {bucketRow("公共 bucket", report.public_bucket)}
                    {bucketRow("草稿 bucket", report.drafts_bucket)}
                    <div className="rounded-lg border border-cafe-espresso/15 bg-cafe-cream/40 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium text-cafe-espresso">推荐 CORS JSON</div>
                            <Button size="sm" variant="outline" onClick={copyJson} data-testid="copy-cors-json">{copied ? "已复制 ✓" : "复制 JSON"}</Button>
                        </div>
                        <p className="text-[12px] text-cafe-espresso/60 mb-2">
                            打开 Cloudflare → R2 → 相应 bucket → <strong>Settings</strong> → <strong>CORS Policy</strong>,粘贴以下 JSON 并保存。两个 bucket 用同一份 JSON。几秒后生效,然后点击下面的"测试上传"验证。
                        </p>
                        <pre className="text-[11px] bg-cafe-espresso text-cafe-cream rounded p-3 overflow-x-auto">{JSON.stringify(report.recommended_cors_json, null, 2)}</pre>
                    </div>

                    {/* --- Live test upload (small file, non-destructive) --- */}
                    <div className="rounded-lg border border-cafe-espresso/15 bg-white p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="text-sm font-medium text-cafe-espresso">测试上传 · 验证 CORS</div>
                                <div className="text-[12px] text-cafe-espresso/60">
                                    用一小段文本从你当前浏览器直接 PUT 到两个 bucket,再由服务器验证并清理。不会写入任何作品或草稿内容,也不会出现在公开页面。
                                </div>
                            </div>
                            <Button onClick={runTest} disabled={testing} data-testid="r2-test-upload-btn">
                                {testing ? "测试中…" : "运行测试"}
                            </Button>
                        </div>
                        {testResult?.fatal && (
                            <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                                测试失败(服务器未响应): {String(testResult.fatal)}
                            </div>
                        )}
                        {testResult && !testResult.fatal && (
                            <div className="space-y-2" data-testid="r2-test-result">
                                {bucketTestRow("公共 bucket · 测试结果", testResult.public)}
                                {bucketTestRow("草稿 bucket · 测试结果", testResult.drafts)}
                                <div className="text-[11.5px] text-cafe-espresso/60">
                                    若 Browser PUT 失败且状态码为 0,就是 CORS 未配置或未生效。粘贴 JSON 后几秒再点一次。
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


function CategoryAuditCard() {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState(null);
    const [savingSlug, setSavingSlug] = React.useState(null);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/case-study-categories/audit`,
                { headers: studioHeaders(), withCredentials: true });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };
    React.useEffect(() => { load(); }, []);

    const assign = async (slug, category) => {
        setSavingSlug(slug);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/case-study-categories/set`,
                { slug, category }, { headers: studioHeaders(), withCredentials: true });
            toast.success(`已归类 → ${category}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "归类失败");
        } finally {
            setSavingSlug(null);
        }
    };

    const Row = ({ p, verdictBadgeClass, verdictLabel }) => {
        const labels = data?.labels || {};
        const ids = data?.canonical_ids || [];
        return (
            <div className="flex items-center justify-between gap-3 py-2 border-b border-cafe-espresso/8 last:border-0" data-testid={`category-row-${p.slug}`}>
                <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-cafe-espresso truncate">
                        {p.title || p.slug}
                        {p.published
                            ? <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">Live</span>
                            : <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5">Draft</span>}
                    </div>
                    <div className="text-[11.5px] text-cafe-espresso/60 flex items-center gap-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] ${verdictBadgeClass}`}>{verdictLabel}</span>
                        <span className="font-mono">raw: "{p.raw || "(empty)"}"</span>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {ids.map((cid) => (
                        <button key={cid} onClick={() => assign(p.slug, cid)} disabled={savingSlug === p.slug}
                            data-testid={`assign-${cid}-to-${p.slug}`}
                            className="text-[10.5px] px-2 py-1 rounded-md border border-cafe-espresso/20 hover:bg-cafe-olive hover:text-cafe-cream hover:border-cafe-olive transition-colors">
                            {labels[cid]?.en || cid}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="rounded-xl border border-cafe-espresso/10 bg-white p-5 md:p-6" data-testid="category-audit-card">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <p className="font-serif text-xl text-cafe-espresso">Case Study 分类审查</p>
                    <p className="text-sm text-cafe-espresso/60 mt-1 max-w-2xl">
                        Studio 和公开 Work 页面共用这 5 个分类。已通过 unambiguous 规则自动迁移的作品不显示;ambiguous 和 unknown 作品需要你点击右侧按钮手动归类。
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? "读取中…" : "刷新"}</Button>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{String(err)}</div>}
            {data && (
                <div className="mt-3 space-y-5">
                    {/* Canonical counters */}
                    <div className="flex flex-wrap gap-2">
                        {(data.canonical_ids || []).map((cid) => (
                            <div key={cid} className="inline-flex items-center gap-2 bg-cafe-cream/60 border border-cafe-espresso/15 rounded-full px-3 py-1 text-[12px]" data-testid={`category-count-${cid}`}>
                                <span className="font-medium text-cafe-espresso">{data.labels[cid]?.en}</span>
                                <span className="text-cafe-espresso/60">· {data.labels[cid]?.cn}</span>
                                <span className="font-mono text-cafe-olive">×{data.canonical_counts[cid] || 0}</span>
                            </div>
                        ))}
                    </div>
                    {/* Ambiguous */}
                    {(data.ambiguous || []).length > 0 && (
                        <div>
                            <p className="text-[12px] uppercase tracking-wider text-amber-800 font-semibold mb-2">
                                需要归类 · 旧分类含义不明确 ({data.ambiguous.length})
                            </p>
                            <div className="rounded-lg border border-amber-200 bg-amber-50/40 px-3">
                                {data.ambiguous.map((p) => (
                                    <Row key={p.slug} p={p}
                                         verdictBadgeClass="bg-amber-200 text-amber-900"
                                         verdictLabel="ambiguous" />
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Unknown */}
                    {(data.unknown || []).length > 0 && (
                        <div>
                            <p className="text-[12px] uppercase tracking-wider text-red-800 font-semibold mb-2">
                                需要归类 · 未识别的分类 ({data.unknown.length})
                            </p>
                            <div className="rounded-lg border border-red-200 bg-red-50/40 px-3">
                                {data.unknown.map((p) => (
                                    <Row key={p.slug} p={p}
                                         verdictBadgeClass="bg-red-200 text-red-900"
                                         verdictLabel="unknown" />
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Empty */}
                    {(data.empty || []).length > 0 && (
                        <div>
                            <p className="text-[12px] uppercase tracking-wider text-cafe-espresso/70 font-semibold mb-2">
                                未设置分类 ({data.empty.length})
                            </p>
                            <div className="rounded-lg border border-cafe-espresso/15 px-3">
                                {data.empty.map((p) => (
                                    <Row key={p.slug} p={p}
                                         verdictBadgeClass="bg-cafe-espresso/15 text-cafe-espresso"
                                         verdictLabel="empty" />
                                ))}
                            </div>
                        </div>
                    )}
                    {((data.ambiguous || []).length + (data.unknown || []).length + (data.empty || []).length === 0) && (
                        <div className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="category-audit-clean">
                            ✓ 所有作品已归入 5 个官方分类,无遗留。
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


export default function WebsiteContentTab() {
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await axios.get(SITE_META);
            setMeta(data);
        } catch (err) {
            toast.error("Failed to load site content");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    if (loading) return <div className="text-sm text-cafe-espresso/60">Loading…</div>;
    if (!meta) return <div className="text-sm text-red-700">Could not load site content.</div>;

    return (
        <div className="space-y-6" data-testid="website-content-tab">
            <div className="rounded-xl bg-cafe-espresso text-cafe-cream p-5 md:p-6">
                <p className="font-serif text-2xl md:text-3xl">Website content</p>
                <p className="text-cafe-cream/70 text-sm mt-2 max-w-2xl">
                    Upload or replace the images that power your public homepage, Services page and Meet Kaily section. Every change is live immediately — refresh the public site to see it. Images are stored on R2 and persist across deploys.
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    <Link to="/studio?tab=site&sub=projects" className="inline-flex items-center gap-1 text-cafe-cream/80 hover:text-cafe-cream border-b border-cafe-cream/30 pb-0.5">
                        Case Study covers & gallery <ArrowRight className="h-3 w-3" />
                    </Link>
                    <Link to="/studio?tab=site&sub=articles" className="inline-flex items-center gap-1 text-cafe-cream/80 hover:text-cafe-cream border-b border-cafe-cream/30 pb-0.5">
                        Journal covers <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
            </div>

            <HeroDualVariantCard meta={meta} onReload={reload} />
            <ServicesHeroDualVariantCard meta={meta} onReload={reload} />
            {SLOTS.map((slot) => (
                <SlotCard key={slot.key} slot={slot} meta={meta} onReload={reload} />
            ))}
            <HeroVideoCard meta={meta} onReload={reload} />
            <HeroVideoDesktopCard meta={meta} onReload={reload} />
            <InternationalShowcaseCard meta={meta} onReload={reload} />
            <MeetKailyTextCard meta={meta} onReload={reload} />
            <AboutContentCard meta={meta} onReload={reload} />
            <HeroFilmUploadDiagnosticsCard />
            <CategoryAuditCard />
        </div>
    );
}
